import type { GroupOption } from '@/scopes/common/components/Filters/GroupedAirportFilter/types'
import { CheckboxState, space, tok } from '@sunwebgroup/ui-components'
import { Checkbox, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'

const NO_OPTIONS: GroupOption[] = []

const Text = styled(Typography)`
  flex:1;
  color: ${tok('semantics_content_color')};
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  padding-top: ${space('primitives_space_12')};
  padding-bottom: ${space('primitives_space_12')};
  gap: ${space('primitives_space_8')};

  &:last-of-type {
    margin-bottom: 0;
  }
`

export function OptionGroup({
  selectedValues,
  value,
  text,
  options = NO_OPTIONS,
  onChange,
  onChangeOption,
}: {
  value: string
  text: string
  selectedValues: string[]
  options: GroupOption[]
  onChange: (checked: CheckboxState) => void
  onChangeOption: ({ id, checked }: { id: string, checked: CheckboxState }) => void
}) {
  const elements = options.map(child => (
    <Option
      key={child.id}
      id={child.id}
      value={child.value}
      selectedValues={selectedValues}
      trailing={child.trailing}
      onChangeOption={onChangeOption}
    />
  ))
  const [selected, setIsSelected] = React.useState<CheckboxState>(false)
  const [halfSelected, setHalfSelected] = React.useState<boolean>(false)

  const myGroupIds = React.useMemo(() => options.map(option => option.id), [options])
  const containsAllIds = React.useMemo(
    () => myGroupIds.every(optionId => selectedValues.includes(optionId)),
    [selectedValues, myGroupIds],
  )

  const containsSomeIds = React.useMemo(
    () => myGroupIds.some(optionId => selectedValues.includes(optionId)),
    [selectedValues, myGroupIds],
  )

  React.useEffect(() => {
    if (containsAllIds) {
      setIsSelected(true)
      setHalfSelected(false)
    }
    else if (containsSomeIds) {
      setIsSelected('indeterminate')
      setHalfSelected(true)
    }
    else {
      setIsSelected(false)
      setHalfSelected(false)
    }
  }, [selectedValues, containsAllIds, containsSomeIds])

  return (
    <Wrapper>
      <CheckboxWrapper>
        <Checkbox
          value={value}
          checked={halfSelected ? 'indeterminate' : selected}
          onCheckedChange={(checked) => {
            setIsSelected(checked)
            setHalfSelected(false)
            onChange(checked)
          }}
        />
        <Text variant="body-m-regular-tight">{text}</Text>
      </CheckboxWrapper>
      {elements}
    </Wrapper>
  )
}

interface OptionProps {
  id: string
  value: string
  selectedValues: string[]
  trailing?: string
  onChangeOption: ({ id, checked }: { id: string, checked: CheckboxState }) => void
}

const OptionWrapper = styled.div`
  margin-left: ${space('primitives_space_24')};
`

const Trailing = styled(Typography)`
  color: ${tok('semantics_content_secondary_color')};
`

function Option({
  id,
  value,
  selectedValues,
  trailing,
  onChangeOption,
}: OptionProps) {
  const [isSelected, setIsSelected] = React.useState<CheckboxState>(false)
  React.useEffect(() => {
    setIsSelected(selectedValues.includes(id))
  }, [selectedValues, id])

  return (
    <OptionWrapper>
      <CheckboxWrapper>
        <Checkbox
          value={id}
          onCheckedChange={checked => onChangeOption({ id, checked })}
          checked={isSelected}
        />
        <Text variant="body-m-regular-tight">{value}</Text>
        {trailing && <Trailing>{trailing}</Trailing>}
      </CheckboxWrapper>
    </OptionWrapper>
  )
}
